import pluralize from 'pluralize';

import {
  DeadlineChipStatus,
  DeadlinesConfigCalcStartDateType,
} from 'app/modules/deadlines/models';

export type TimeOptionsType = 'h' | 'd' | 'w' | 'm';

export function timeToSeconds(
  value: number,
  timeOption: TimeOptionsType,
): number {
  switch (timeOption) {
    case 'h':
      return value * 60 * 60;
    case 'd':
      return value * 60 * 60 * 24;
    case 'w':
      return value * 60 * 60 * 24 * 7;
    case 'm':
      return value * 60 * 60 * 24 * 31;
    default:
      return value;
  }
}

export function secondsToTime(
  seconds: number,
  timeOption: TimeOptionsType,
): number {
  switch (timeOption) {
    case 'h':
      return Math.floor(seconds / 60 / 60);
    case 'd':
      return Math.floor(seconds / 60 / 60 / 24);
    case 'w':
      return Math.floor(seconds / 60 / 60 / 24 / 7);
    case 'm':
      return Math.floor(seconds / 60 / 60 / 24 / 31);
    default:
      return seconds;
  }
}

export function getInitialTimeOption(seconds: number): TimeOptionsType {
  // Calculate the most appropriate time unit
  if (seconds % (60 * 60 * 24 * 31) === 0) {
    return 'm';
  }
  if (seconds % (60 * 60 * 24 * 7) === 0) {
    return 'w';
  }
  if (seconds % (60 * 60 * 24) === 0) {
    return 'd';
  }
  return 'h';
}

export function getFullTimeLabel(seconds: number): string {
  const timeBucket = getInitialTimeOption(seconds);
  const timeAmount = secondsToTime(seconds, timeBucket);
  switch (timeBucket) {
    case 'h':
      return pluralize('hour', timeAmount, true);
    case 'd':
      return pluralize('day', timeAmount, true);
    case 'w':
      return pluralize('week', timeAmount, true);
    case 'm':
      return pluralize('month', timeAmount, true);
    default: {
      const exhaustivenessCheck: never = timeBucket;
      return exhaustivenessCheck;
    }
  }
}

export const getDeadlineChipStatus = (
  deadline?: number,
  pastDue?: boolean,
  showWarning?: boolean,
): DeadlineChipStatus => {
  if (!deadline) return DeadlineChipStatus.NO_DEADLINE;
  if (pastDue) return DeadlineChipStatus.PAST_DEADLINE;
  if (showWarning) return DeadlineChipStatus.WARN_BEFORE_DEADLINE;
  return DeadlineChipStatus.NORMAL_DEADLINE;
};

export const getFullDeadlineLabel = (
  seconds: number,
  calcStartDate: DeadlinesConfigCalcStartDateType,
) => {
  switch (calcStartDate) {
    case 'CREATED_AT':
      return `${getFullTimeLabel(seconds)} after creation`;
    case 'LAST_EVENT':
      return `${getFullTimeLabel(seconds)} after latest event creation`;
    case 'EARLIEST_EVENT':
      return `${getFullTimeLabel(seconds)} after earliest event creation`;
    default: {
      const exhaustivenessCheck: never = calcStartDate;
      return exhaustivenessCheck;
    }
  }
};
